/**
 * =====================================================================================
 * CONFIGURACIÓN PRINCIPAL DEL STORE VUEX - index.js
 * =====================================================================================
 *
 * Este archivo configura Vuex, el sistema de gestión de estado centralizado de Vue.
 *
 * ¿QUÉ ES VUEX?
 * -------------
 * Vuex es una librería para gestionar el "estado" de tu aplicación Vue.
 * El "estado" son los datos que tu aplicación necesita compartir entre componentes.
 *
 * PROBLEMA QUE RESUELVE:
 * ----------------------
 * Imagina que tienes 10 componentes que necesitan saber quién es el usuario actual.
 *
 * SIN VUEX (problemático):
 * - Cada componente debe obtener los datos del usuario
 * - Si el usuario cambia, debes notificar a los 10 componentes
 * - Datos duplicados, fácil de perder sincronización
 *
 * CON VUEX (solución):
 * - Un solo lugar (store) guarda el usuario
 * - Todos los componentes leen del mismo lugar
 * - Cualquier cambio actualiza automáticamente todos los componentes
 *
 * CONCEPTOS CLAVE DE VUEX:
 * ------------------------
 *
 * 1. STATE (Estado):
 *    Objeto que contiene todos los datos de la aplicación.
 *    Es la "fuente de verdad" única.
 *
 *    state: {
 *      user: null,
 *      productos: []
 *    }
 *
 * 2. GETTERS (Computados del Estado):
 *    Funciones que obtienen datos del state, posiblemente transformándolos.
 *    Similar a computed properties pero para el store.
 *
 *    getters: {
 *      isLoggedIn: state => !!state.user,
 *      productosActivos: state => state.productos.filter(p => p.activo)
 *    }
 *
 * 3. MUTATIONS (Mutaciones):
 *    ÚNICAS funciones que pueden modificar el state.
 *    Deben ser síncronas (sin await).
 *
 *    mutations: {
 *      SET_USER(state, user) {
 *        state.user = user
 *      }
 *    }
 *
 * 4. ACTIONS (Acciones):
 *    Funciones asíncronas que pueden llamar a APIs y luego commit mutations.
 *    Son el "puente" entre servicios HTTP y el state.
 *
 *    actions: {
 *      async login({ commit }, credentials) {
 *        const user = await authService.login(credentials)
 *        commit('SET_USER', user)
 *      }
 *    }
 *
 * 5. MODULES (Módulos):
 *    Dividen el store en secciones manejables.
 *    Cada módulo tiene su propio state, getters, mutations, actions.
 *
 * FLUJO DE DATOS EN VUEX:
 * -----------------------
 *
 *   Componente Vue
 *        │
 *        ▼ dispatch('login', credentials)
 *   ┌──────────┐
 *   │ ACTIONS  │ ← Llama API, lógica de negocio
 *   └────┬─────┘
 *        │ commit('SET_USER', userData)
 *        ▼
 *   ┌────────────┐
 *   │ MUTATIONS  │ ← Modifica el state
 *   └────┬───────┘
 *        │
 *        ▼
 *   ┌─────────┐
 *   │  STATE  │ ← Fuente de verdad
 *   └────┬────┘
 *        │
 *        ▼ Reactividad de Vue
 *   ┌──────────┐
 *   │ GETTERS  │ ← Datos computados
 *   └────┬─────┘
 *        │
 *        ▼
 *   Componente se actualiza automáticamente
 *
 * ESTRUCTURA DE MÓDULOS EN ESTA APP:
 * ----------------------------------
 *
 * store/
 * ├── index.js (este archivo) ← Store principal + action initApp
 * └── modules/
 *     ├── auth.js        ← Autenticación (login, logout, token)
 *     ├── emisor.js      ← Emisores y sucursales
 *     ├── documentos.js  ← Facturas/DTEs emitidos
 *     └── catalogos.js   ← Catálogos del MH
 *
 * =====================================================================================
 */

// Importamos createStore de Vuex 4 (compatible con Vue 3)
// createStore es una función que crea una nueva instancia del store
import { createStore } from 'vuex'

// =====================================================================================
// IMPORTACIÓN DE MÓDULOS
// =====================================================================================
//
// Cada módulo maneja una "área" específica de la aplicación.
// Los módulos permiten organizar el código y evitar un archivo gigante.

/**
 * Módulo de autenticación
 * @see ./modules/auth.js
 * Maneja: login, logout, token JWT, datos del usuario
 */
import auth from './modules/auth'

/**
 * Módulo del emisor
 * @see ./modules/emisor.js
 * Maneja: datos de la empresa, sucursales, establecimiento actual
 */
import emisor from './modules/emisor'

/**
 * Módulo de documentos
 * @see ./modules/documentos.js
 * Maneja: facturas emitidas, documento en edición, filtros, estadísticas
 */
import documentos from './modules/documentos'

/**
 * Módulo de catálogos
 * @see ./modules/catalogos.js
 * Maneja: catálogos del MH (departamentos, municipios, tributos, etc.)
 */
import catalogos from './modules/catalogos'

/**
 * Módulo de tema/apariencia
 * @see ./modules/theme.js
 * Maneja: tema claro/oscuro, variantes de color, persistencia en localStorage
 */
import theme from './modules/theme'

// =====================================================================================
// CREACIÓN DEL STORE
// =====================================================================================

export default createStore({
  /**
   * ===========================================================================
   * STATE RAÍZ (Estado Global)
   * ===========================================================================
   *
   * Datos que no pertenecen a ningún módulo específico.
   * Normalmente son datos muy generales de la aplicación.
   */
  state: {
    // Nombre de la aplicación (se muestra en header, título, etc.)
    appName: 'Sistema DTE El Salvador',

    // Versión de la aplicación
    version: '1.0.0',

    // Indica si la app está cargando datos iniciales (emisor, catálogos, etc.)
    appLoading: false
  },

  /**
   * ===========================================================================
   * GETTERS RAÍZ
   * ===========================================================================
   *
   * Getters para acceder al state raíz.
   *
   * USO EN COMPONENTES:
   * -------------------
   * import { useStore } from 'vuex'
   * const store = useStore()
   * const appName = store.getters.appName
   */
  getters: {
    /**
     * Retorna el nombre de la aplicación
     * @returns {string} Nombre de la app
     */
    appName: state => state.appName,

    /**
     * Retorna la versión de la aplicación
     * @returns {string} Versión en formato X.Y.Z
     */
    version: state => state.version,

    appLoading: state => state.appLoading
  },

  /**
   * ===========================================================================
   * MUTATIONS RAÍZ
   * ===========================================================================
   *
   * Actualmente vacío porque no hay mutaciones globales.
   * El estado raíz (appName, version) es inmutable.
   */
  mutations: {
    SET_APP_LOADING(state, loading) {
      state.appLoading = loading
    }
  },

  /**
   * ===========================================================================
   * ACTIONS RAÍZ
   * ===========================================================================
   *
   * Actions globales que coordinan múltiples módulos.
   */
  actions: {
    /**
     * =========================================================================
     * ACTION: initApp
     * =========================================================================
     *
     * Inicializa la aplicación al arrancar.
     * Esta es la action más importante - se llama cuando la app inicia.
     *
     * PROCESO:
     * 1. Intentar restaurar sesión desde localStorage (si el usuario ya había iniciado sesión)
     * 2. Si hay sesión válida, cargar todos los datos necesarios en paralelo
     *
     * ¿POR QUÉ CARGAR EN PARALELO?
     * ----------------------------
     * Promise.all() ejecuta múltiples promesas simultáneamente.
     * En lugar de:
     *   await fetchEmisores()      // 500ms
     *   await fetchDocumentos()    // 500ms
     *   await fetchCatalogos()     // 500ms
     *   // Total: 1500ms
     *
     * Usamos:
     *   await Promise.all([fetchEmisores, fetchDocumentos, fetchCatalogos])
     *   // Total: ~500ms (todas al mismo tiempo)
     *
     * CONTEXTO DE dispatch:
     * ---------------------
     * dispatch() llama acciones. Cuando el action tiene namespace (módulo),
     * usamos 'modulo/action':
     * - dispatch('auth/restoreSession') → llama restoreSession en módulo auth
     * - dispatch('emisor/fetchEmisores') → llama fetchEmisores en módulo emisor
     *
     * @param {Object} context - Contexto de Vuex con dispatch
     * @returns {boolean} true si había sesión válida, false si no
     *
     * USO:
     * ----
     * // En main.js o App.vue
     * const hasSession = await store.dispatch('initApp')
     * if (!hasSession) {
     *   router.push('/login')
     * }
     */
    async initApp({ commit, dispatch, getters }) {
      // PASO 1: Intentar restaurar sesión existente
      // restoreSession lee el token de localStorage y valida si aún es válido
      const hasSession = await dispatch('auth/restoreSession')

      // PASO 2: Si hay sesión, cargar datos necesarios
      if (hasSession) {
        commit('SET_APP_LOADING', true)
        // Verificar si es SuperAdmin para evitar llamadas innecesarias
        const userRole = getters['auth/userRole']
        const esSuperAdmin = userRole === 'SuperAdmin'

        // Preparar las promesas a ejecutar
        const promesas = [
          // Cargar emisores disponibles y establecer el actual
          dispatch('emisor/fetchEmisores'),

          // Cargar catálogos básicos del MH (departamentos, municipios, etc.)
          dispatch('catalogos/fetchCatalogosBasicos')
        ]

        // Solo cargar documentos si NO es SuperAdmin
        // SuperAdmin no tiene documentos asignados
        if (!esSuperAdmin) {
          promesas.push(dispatch('documentos/fetchDocumentos'))
          // Cargar establecimientos del usuario (necesario para emisión de facturas)
          promesas.push(dispatch('emisor/fetchEstablecimientosUsuario'))
        }

        // Cargar datos iniciales en paralelo para mejor rendimiento
        try {
          await Promise.all(promesas)
        } finally {
          commit('SET_APP_LOADING', false)
        }
      }

      // Retornar si había sesión para que el llamador sepa si debe redirigir
      return hasSession
    }
  },

  /**
   * ===========================================================================
   * MODULES (Módulos)
   * ===========================================================================
   *
   * Registramos los módulos importados.
   * Cada módulo queda "namespaced" automáticamente si tiene namespaced: true.
   *
   * NAMESPACING:
   * ------------
   * Sin namespace: store.commit('SET_USER', user)
   * Con namespace: store.commit('auth/SET_USER', user)
   *
   * El namespace evita colisiones si dos módulos tienen una mutation con el mismo nombre.
   *
   * ACCESO A MÓDULOS DESDE COMPONENTES:
   * -----------------------------------
   *
   * // Getters
   * store.getters['auth/isAuthenticated']
   * store.getters['catalogos/departamentos']
   *
   * // State directo
   * store.state.auth.user
   * store.state.catalogos.departamentos
   *
   * // Mutations
   * store.commit('auth/SET_USER', user)
   *
   * // Actions
   * store.dispatch('auth/login', credentials)
   * store.dispatch('catalogos/fetchCatalogosBasicos')
   *
   * // Con mapGetters/mapActions (Options API)
   * computed: {
   *   ...mapGetters('auth', ['isAuthenticated', 'currentUser'])
   * },
   * methods: {
   *   ...mapActions('auth', ['login', 'logout'])
   * }
   */
  modules: {
    auth,       // Disponible como store.state.auth, store.dispatch('auth/...')
    emisor,     // Disponible como store.state.emisor, store.dispatch('emisor/...')
    documentos, // Disponible como store.state.documentos, store.dispatch('documentos/...')
    catalogos,  // Disponible como store.state.catalogos, store.dispatch('catalogos/...')
    theme       // Disponible como store.state.theme, store.dispatch('theme/...')
  }
})

/**
 * =====================================================================================
 * USO DEL STORE EN COMPONENTES
 * =====================================================================================
 *
 * COMPOSITION API (recomendado en Vue 3):
 * ---------------------------------------
 * <script setup>
 * import { useStore } from 'vuex'
 * import { computed } from 'vue'
 *
 * const store = useStore()
 *
 * // Acceder a state
 * const user = computed(() => store.state.auth.user)
 *
 * // Acceder a getters
 * const isAuth = computed(() => store.getters['auth/isAuthenticated'])
 *
 * // Dispatch actions
 * const handleLogin = async () => {
 *   await store.dispatch('auth/login', { usuario, password, ambiente })
 * }
 *
 * // Commit mutations (poco común, normalmente usas actions)
 * store.commit('auth/SET_USER', userData)
 * </script>
 *
 * OPTIONS API:
 * ------------
 * <script>
 * import { mapGetters, mapActions } from 'vuex'
 *
 * export default {
 *   computed: {
 *     // Mapear getters del módulo 'auth'
 *     ...mapGetters('auth', ['isAuthenticated', 'currentUser']),
 *
 *     // Getter manual
 *     departamentos() {
 *       return this.$store.state.catalogos.departamentos
 *     }
 *   },
 *   methods: {
 *     // Mapear actions del módulo 'auth'
 *     ...mapActions('auth', ['login', 'logout']),
 *
 *     // Action manual
 *     async cargarCatalogos() {
 *       await this.$store.dispatch('catalogos/fetchCatalogosBasicos')
 *     }
 *   }
 * }
 * </script>
 *
 * =====================================================================================
 */
